import { arrayMove } from '@dnd-kit/sortable';
import { PlanningEntity } from 'src/entities/planningEntity';

import { CycleOperations } from './sortedOperationIds';

/** operation id → 回転番号 (1 始まり)。API の order_operation_cycle_indexes */
export type CycleIndexMap = { [operationId: number]: number };

/**
 * 回転を並べ替えたときに送る operation 列と回転番号を作る。
 *
 * V3Plans (presenters/PlanningsCycles.presenter.tsx:157-198) と同じく、
 * 動かしたあとの並びで回転番号を 1 から振り直す。
 */
export function buildCycleReorderPayload(
  cycles: readonly CycleOperations[],
  fromCycleIndex: number,
  toCycleIndex: number,
): { sortedOperationIds: number[]; orderOperationCycleIndexes: CycleIndexMap } {
  const from = cycles.findIndex((it) => it.cycleIndex === fromCycleIndex);
  const to = cycles.findIndex((it) => it.cycleIndex === toCycleIndex);
  // 掴んだ側・落とした側のどちらかが引けなければ動かさない（並びは今のまま返す）
  const moved = from < 0 || to < 0 ? [...cycles] : arrayMove([...cycles], from, to);

  const sortedOperationIds: number[] = [];
  const orderOperationCycleIndexes: CycleIndexMap = {};
  moved.forEach((cycle, index) => {
    cycle.places.forEach((place) => {
      place.operationIds.forEach((operationId) => {
        sortedOperationIds.push(operationId);
        // 回転番号は「何番目か」。元の cycleIndex は動かしたあと飛び番になる
        orderOperationCycleIndexes[operationId] = index + 1;
      });
    });
  });

  return { sortedOperationIds, orderOperationCycleIndexes };
}

export type ReorderPlanningContext = {
  startOn: string;
  endOn: string;
  orderOperationIdsForSort: number[];
  /** 回転を動かしたときだけ渡す。訪問順の並べ替えでは回転が変わらない */
  orderOperationCycleIndexes?: CycleIndexMap;
  loadAfterInitialStock: boolean;
  setCurrentTimeToAlgorithm: boolean;
};

/**
 * 並べ替えを保存するときの PlanningEntity。
 *
 * 保存は専用 API ではなく自動配車 API の順序固定実行なので、V3Plans
 * (Component.tsx:867-915 requestSingleAlgorithmPlanning) と同じ値を送る。
 *
 * Why not buildPlanningEntity: あちらは自動配車ダイアログの設定を写す。
 * 緩和や時間拡張が入ったままだと、並べ替えを保存しただけで頼んでいない
 * 計画変更が起きる。
 */
export function buildReorderPlanningEntity(ctx: ReorderPlanningContext): PlanningEntity {
  return {
    startOn: ctx.startOn,
    endOn: ctx.endOn,
    lockType: 'sequence',
    isRespectRecent: false,
    excludeToll: false,
    respectType: 'day_of_week',
    respectDaysOfWeek: [],
    respectOn: '',
    isTimeTableBackward: false,
    includeFutureDeliveries: true,
    activateConstraintLoadAfterNotCleared: false,
    orderOperationIdsForSort: ctx.orderOperationIdsForSort,
    orderOperationCycleIndexes: ctx.orderOperationCycleIndexes,
    deleteOrderIdsFromOperations: [],
    selectedRelaxedRules: [],
    priorityLargeTrucks: 0.5,
    expandWorkingStartTime: 0,
    expandWorkingEndTime: 0,
    expandLoadStartTime: 0,
    expandLoadEndTime: 0,
    expandUnloadStartTime: 0,
    expandUnloadEndTime: 0,
    expandMaxVolumeRate: 0,
    expandMaxLoadCapacityRate: 0,
    requestAsync: false,
    mlSourceType: -1,
    balancedLoading: false,
    autoSplitOrders: false,
    dontExchangePreroute: true,
    concurrentAllOrNothing: false,
    loadAfterInitialStock: ctx.loadAfterInitialStock,
    setCurrentTimeToAlgorithm: ctx.setCurrentTimeToAlgorithm,
    mlPlanning: true,
    optimizeForLoad: false,
    forceNullBase: false,
    trunkTransportation: false,
    activateIncrementalMaxNumPistons: false,
    traceMode: false,
    traceOnly: false,
    traceCompanyId: 0,
    senario: 'setcover',
    disableGlobalOpt: false,
  };
}
